import { RemoveScroll } from '@mantine/core';
import clsx from 'clsx';
import React, { ComponentProps, useMemo } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { darkModeRoot } from 'src/app/App.css';
import { ClientLogContextProvider } from 'src/contexts/ClientLogContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { ActionOutboxEntityType } from 'src/WebApiController';

import * as styles from './Modal.css';
import { ModalBody, ModalFooter, StyledModal } from './Modal.styled';
import { ModalHeader } from './ModalHeader';

const SlideInSize = [
  'slide-in',
  'slide-in-md',
  'slide-in-lg',
  'slide-in-full',
  'slide-left',
  'slide-left-sm',
];

export type FullControlModalProps = Omit<
  ComponentProps<typeof RSModal>,
  'Body' | 'Header' | 'Footer' | 'ContentWrapper'
> & { children: React.ReactNode };

export type ModalProps =
  | ({
      Body: React.ComponentType | JSX.Element;
      Header?: React.ComponentType | JSX.Element;
      Footer?: React.ComponentType | JSX.Element;
      ContentWrapper?: React.FC;
      size?:
        | 'sm'
        | 'lg'
        | 'xl'
        | 'fullscreen'
        | 'slide-in'
        | 'slide-in-md'
        | 'slide-in-lg'
        | 'slide-in-full'
        | 'slide-left'
        | 'slide-left-sm';
      /**
       * Whether to dismiss the dialog when clicking outside of its boundary
       * For message box, setting this to true is the norm.
       * For wizards, we do not want user to accidentally dismiss the entire dialog by clicking elsewhere.
       * Therefore, set this to true with care.
       */
      clickOutsideToClose?: boolean;
      /* When set, the url is appended with a query param <deepLinkKey>=<deepLinkValue> to allow a deep link visible on the browser's address bar */
      deepLinkKey?: string;
      deepLinkValue?: string | number;
      trackViewEntityType?: ActionOutboxEntityType;
      onCloseOverride?: () => void;
      onExit?: () => void;
    } & ComponentProps<typeof RSModal>)
  | FullControlModalProps;

export const Modal = ({
  Header,
  Body,
  Footer,
  isOpen,
  onClose,
  children,
  clickOutsideToClose,
  backdrop,
  scrollable = true,
  size = 'lg',
  className,
  ...rest
}: Omit<ModalProps, 'fullscreen'>) => {
  const hasChildren = React.Children.count(children) > 0;
  const commonModalProps = {
    isOpen: isOpen,
    toggle: onClose,
  };
  const { isDarkMode } = useSiteTheme();
  const isMobile = useMatchMedia('mobile');

  // Infer the fullscreen prop from the size to avoid conflict and default to sm
  const inferredFullscreen = useMemo((): ModalProps['fullscreen'] => {
    // Slide in should not use fullscreen
    if (SlideInSize.includes(size)) {
      return false;
    }
    if (size === 'fullscreen') {
      return true;
    }
    return 'sm';
  }, [size]);

  // Limite to the new mobile UI, but this should be generic for mobile
  // This should also be safe to roll out to all, but need verification
  if (isMobile) {
    // Lock scroll for the Modal content, Modal content usually is meant to be top level
    return (
      <ClientLogContextProvider>
        <RemoveScroll allowPinchZoom enabled={isOpen}>
          <StyledModal
            {...commonModalProps}
            backdrop={clickOutsideToClose ? backdrop : 'static'}
            backdropClassName={styles.backdrop}
            size={size}
            fullscreen={inferredFullscreen}
            scrollable={scrollable}
            keyboard
            className={clsx(className, {
              [darkModeRoot]: isDarkMode,
            })}
            contentClassName={styles.modal}
            {...rest}
          >
            {hasChildren ? (
              children
            ) : (
              <>
                {Header ? (
                  <ModalHeader onClose={onClose}>
                    {asJsxElement(Header)}
                  </ModalHeader>
                ) : null}
                <ModalBody>{asJsxElement(Body)}</ModalBody>
                {Footer ? (
                  <ModalFooter>{asJsxElement(Footer)}</ModalFooter>
                ) : null}
              </>
            )}
          </StyledModal>
        </RemoveScroll>
      </ClientLogContextProvider>
    );
  }

  return (
    <ClientLogContextProvider>
      <StyledModal
        {...commonModalProps}
        backdrop={clickOutsideToClose ? backdrop : 'static'}
        backdropClassName={styles.backdrop}
        size={size}
        fullscreen={inferredFullscreen}
        scrollable={scrollable}
        keyboard
        className={clsx(className, {
          [darkModeRoot]: isDarkMode,
        })}
        contentClassName={styles.modal}
        {...rest}
      >
        {hasChildren ? (
          children
        ) : (
          <>
            {Header ? (
              <ModalHeader onClose={onClose}>
                {asJsxElement(Header)}
              </ModalHeader>
            ) : null}
            <ModalBody>{asJsxElement(Body)}</ModalBody>
            {Footer ? <ModalFooter>{asJsxElement(Footer)}</ModalFooter> : null}
          </>
        )}
      </StyledModal>
    </ClientLogContextProvider>
  );
};

const asJsxElement = (Element: React.ComponentType | JSX.Element) =>
  typeof Element === 'function' ? <Element /> : Element;
